package com.example.positionahead.domain

import com.example.positionahead.model.FinancialGoal
import com.example.positionahead.model.PortfolioProjectionPoint
import com.example.positionahead.model.PortfolioSummary
import com.example.positionahead.model.effectiveBondTarget
import com.example.positionahead.model.effectiveStockTarget
import com.example.positionahead.util.formatCurrency
import kotlin.math.abs

enum class PlanPortfolioSide {
    CURRENT,
    SUGGESTED,
    TIE,
}

data class PlanComparisonRow(
    val metric: String,
    val currentDisplay: String,
    val suggestedDisplay: String,
    val winner: PlanPortfolioSide,
    val whyItMatters: String,
)

data class PlanComparisonVerdict(
    val overallWinner: PlanPortfolioSide,
    val headline: String,
    val detail: String,
    val suggestedScore: Int,
    val currentScore: Int,
    val rows: List<PlanComparisonRow>,
)

object PlanPortfolioComparer {

    fun compare(
        current: PortfolioSummary,
        suggested: PortfolioSummary,
        goal: FinancialGoal,
        projections: List<PortfolioProjectionPoint>,
    ): PlanComparisonVerdict {
        val rows = mutableListOf<PlanComparisonRow>()

        val currentStockDrift = abs(current.stockAllocationPercent - goal.effectiveStockTarget())
        val suggestedStockDrift = abs(suggested.stockAllocationPercent - goal.effectiveStockTarget())
        rows += row(
            metric = "Stock/bond mix vs goal",
            currentDisplay = "${current.stockAllocationPercent.format(1)}% stocks (Δ${currentStockDrift.format(1)}%)",
            suggestedDisplay = "${suggested.stockAllocationPercent.format(1)}% stocks (Δ${suggestedStockDrift.format(1)}%)",
            lowerIsBetterCurrent = currentStockDrift,
            lowerIsBetterSuggested = suggestedStockDrift,
            why = "Closer to your ${goal.effectiveStockTarget()}% / ${goal.effectiveBondTarget()}% target.",
        )

        val currentMax = current.positions.maxOfOrNull { it.portfolioWeightPercent } ?: 0.0
        val suggestedMax = suggested.positions.maxOfOrNull { it.portfolioWeightPercent } ?: 0.0
        rows += row(
            metric = "Largest single position",
            currentDisplay = "${currentMax.format(1)}% (max ${goal.maxSinglePositionPercent.format(0)}%)",
            suggestedDisplay = "${suggestedMax.format(1)}%",
            lowerIsBetterCurrent = currentMax,
            lowerIsBetterSuggested = suggestedMax,
            why = "Lower concentration reduces single-stock risk.",
        )

        rows += row(
            metric = "Est. annual dividend income",
            currentDisplay = formatCurrency(current.estimatedAnnualIncome),
            suggestedDisplay = formatCurrency(suggested.estimatedAnnualIncome),
            higherIsBetterCurrent = current.estimatedAnnualIncome,
            higherIsBetterSuggested = suggested.estimatedAnnualIncome,
            why = "Income from FMP yield estimates on each holding.",
        )

        val horizon = projections.maxByOrNull { it.months }
        if (horizon != null) {
            rows += row(
                metric = "${horizon.months}-month illustrative value",
                currentDisplay = formatCurrency(horizon.currentPortfolioValue),
                suggestedDisplay = formatCurrency(horizon.suggestedPortfolioValue),
                higherIsBetterCurrent = horizon.currentPortfolioValue,
                higherIsBetterSuggested = horizon.suggestedPortfolioValue,
                why = "Trend + dividends + ${formatCurrency(goal.monthlyContribution)}/mo — not a forecast.",
            )
        }

        val currentPositions = current.positions.count { it.portfolioWeightPercent >= 2.0 }
        val suggestedPositions = suggested.positions.count { it.portfolioWeightPercent >= 2.0 }
        rows += row(
            metric = "Meaningful positions (≥2%)",
            currentDisplay = "$currentPositions",
            suggestedDisplay = "$suggestedPositions",
            higherIsBetterCurrent = currentPositions.toDouble(),
            higherIsBetterSuggested = suggestedPositions.toDouble(),
            why = "More balanced sleeves when no single name dominates.",
        )

        val suggestedWins = rows.count { it.winner == PlanPortfolioSide.SUGGESTED }
        val currentWins = rows.count { it.winner == PlanPortfolioSide.CURRENT }
        val overall = when {
            suggestedWins > currentWins -> PlanPortfolioSide.SUGGESTED
            currentWins > suggestedWins -> PlanPortfolioSide.CURRENT
            else -> PlanPortfolioSide.TIE
        }

        val headline = when (overall) {
            PlanPortfolioSide.SUGGESTED ->
                "Suggested portfolio fits your goals better ($suggestedWins of ${rows.size} checks)"
            PlanPortfolioSide.CURRENT ->
                "Current portfolio is already close ($currentWins of ${rows.size} checks favor staying)"
            PlanPortfolioSide.TIE ->
                "Current and suggested are evenly matched — review trades below"
        }

        val detail = when (overall) {
            PlanPortfolioSide.SUGGESTED ->
                "Rebalancing toward the suggested mix improves alignment with the goals you set in Settings."
            PlanPortfolioSide.CURRENT ->
                "You may still tweak goals or add Discover names, but you don't need large moves to stay on plan."
            PlanPortfolioSide.TIE ->
                "Small adjustments or new contributions may tip the balance — use the holdings tables to decide."
        }

        return PlanComparisonVerdict(
            overallWinner = overall,
            headline = headline,
            detail = detail,
            suggestedScore = suggestedWins,
            currentScore = currentWins,
            rows = rows,
        )
    }

    private fun row(
        metric: String,
        currentDisplay: String,
        suggestedDisplay: String,
        why: String,
        lowerIsBetterCurrent: Double? = null,
        lowerIsBetterSuggested: Double? = null,
        higherIsBetterCurrent: Double? = null,
        higherIsBetterSuggested: Double? = null,
    ): PlanComparisonRow {
        val winner = when {
            lowerIsBetterCurrent != null && lowerIsBetterSuggested != null -> {
                when {
                    lowerIsBetterSuggested < lowerIsBetterCurrent - 0.05 -> PlanPortfolioSide.SUGGESTED
                    lowerIsBetterCurrent < lowerIsBetterSuggested - 0.05 -> PlanPortfolioSide.CURRENT
                    else -> PlanPortfolioSide.TIE
                }
            }
            higherIsBetterCurrent != null && higherIsBetterSuggested != null -> {
                when {
                    higherIsBetterSuggested > higherIsBetterCurrent + 0.05 -> PlanPortfolioSide.SUGGESTED
                    higherIsBetterCurrent > higherIsBetterSuggested + 0.05 -> PlanPortfolioSide.CURRENT
                    else -> PlanPortfolioSide.TIE
                }
            }
            else -> PlanPortfolioSide.TIE
        }
        return PlanComparisonRow(
            metric = metric,
            currentDisplay = currentDisplay,
            suggestedDisplay = suggestedDisplay,
            winner = winner,
            whyItMatters = why,
        )
    }

    private fun Double.format(decimals: Int): String {
        val factor = 10.0.pow(decimals)
        return (kotlin.math.round(this * factor) / factor).toString()
    }

    private fun Double.pow(n: Int): Double {
        var result = 1.0
        repeat(n) { result *= 10.0 }
        return result
    }
}
